import type { ApiAgentCommand } from "../../data/api/chat";

// The composer's `/` completion, as pure functions over the draft
// (chat.md §Slash commands). The keyboard lives in the composer; what a given
// draft is asking for lives here, where it can be reasoned about on its own.

/**
 * The command name the composer is in the middle of typing, or `null`.
 *
 * A prompt is a command only when the **whole draft so far** is one word
 * starting with `/`, which is the same rule the agent's CLI applies on the way
 * in: a slash anywhere else is prose, and `/review origin/main` is the `review`
 * command with an argument rather than a request to complete `main`. So the
 * menu opens on the first `/` and closes on the space that ends the name.
 *
 * `/` on its own answers with the empty string — every command matches, which
 * is what makes the bare slash a way to see what the session offers.
 */
export const commandFragment = (draft: string): string | null =>
  /^\/(\S*)$/.exec(draft)?.[1] ?? null;

/** Every name the command answers to, canonical first. */
const namesOf = (command: ApiAgentCommand): ReadonlyArray<string> => [
  command.name,
  ...command.aliases,
];

/**
 * The commands worth offering for `fragment`, best match first.
 *
 * Matched on names *and* aliases, so `/cost` finds the command it resolves to
 * rather than nothing. Prefix matches rank above the rest — a reader typing
 * `re` means `review` far more often than `pr-review` — and within each group
 * the session's own order is kept, since the CLI already lists its commands the
 * way it wants them read.
 */
export const matchCommands = (
  commands: ReadonlyArray<ApiAgentCommand>,
  fragment: string,
): ReadonlyArray<ApiAgentCommand> => {
  const needle = fragment.toLowerCase();
  const prefix: ApiAgentCommand[] = [];
  const rest: ApiAgentCommand[] = [];
  for (const command of commands) {
    const names = namesOf(command).map((name) => name.toLowerCase());
    if (names.some((name) => name.startsWith(needle))) {
      prefix.push(command);
    } else if (names.some((name) => name.includes(needle))) {
      rest.push(command);
    }
  }
  return [...prefix, ...rest];
};

/**
 * The draft after completing to `command`.
 *
 * A command that takes arguments is left with the caret after a space, ready
 * for them; one that takes none is completed to exactly what would be sent, so
 * the very next Enter sends it rather than a name with a stray trailing space.
 */
export const completeCommand = (command: ApiAgentCommand): string =>
  command.argument_hint ? `/${command.name} ` : `/${command.name}`;
